/**
 * What a finished call left behind.
 *
 * ── The outcome is stored once and worded twice ─────────────────────────────
 * `missed` and `declined` are the same row read from two seats. The record is
 * written by whoever *placed* the call, so `own` — `senderId === me.id` — is
 * the seat, not the authorship, and it is the whole difference between "No
 * answer" and "Missed call". The desktop app keeps this in one function
 * (`relay/src/data/types.h`, `callSummary`) for a reason worth repeating: the
 * transcript row and the list's preview line both say it, and a call that read
 * "Missed call" in one and "No answer" in the other would be the same event
 * contradicting itself half an inch away.
 *
 * Every string in it is generated here. Nothing a stranger typed reaches it.
 */

import { ds } from '../../ds';
import { useT } from '../../i18n';
import { formatDuration, formatTime } from '../../i18n/format';
import type { MessageCall } from '../../lib/relay/types';
import './chat.css';

/** The sentence, from one end's seat. `t` is passed rather than hooked so the
 *  list's preview line can call it too, from outside a component. */
export function callSummary(
  t: (key: string, vars?: Record<string, string | number>) => string,
  call: MessageCall,
  own: boolean
): string {
  if (call.outcome === 'answered') {
    return t('conversation.call.answered', { duration: formatDuration(call.durationSeconds) });
  }
  if (call.outcome === 'declined') return t('conversation.call.declined');
  return own ? t('conversation.call.noanswer') : t('conversation.call.missed');
}

export function CallRow({ call, at, own }: { call: MessageCall; at: number; own: boolean }) {
  const { Icon } = ds();
  const t = useT();

  return (
    <div className="rl-event">
      <Icon name={call.camera ? 'CameraOn' : 'Call'} size={16} />
      <span>{callSummary(t, call, own)}</span>
      <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>{formatTime(at)}</span>
    </div>
  );
}
